import { z } from '../zod';
import { ObjectIdStringValidationSchema } from '../shared/ObjectId';
import { hasAtLeastOneField } from '../shared/refines';
import {
  TicketTypeInputValidationSchema,
  withFreeTicketTypePriceCheck,
} from '../event-page/create';
import {
  TICKET_TYPE_IMAGE_ID_DESCRIPTION,
  TICKET_TYPE_TRIGGER_ID_DESCRIPTION,
  TicketTypeEventIdInputSchema,
  TicketTypeIdInputSchema,
  TicketTypePathParamsValidationSchema,
} from './params';
import { TicketTypeShowHideLogicInputValidationSchema } from './showHideLogic';

export const UpdateTicketTypeParamsValidationSchema = TicketTypePathParamsValidationSchema;

// Only catches `pricing: "free"` and a price sent together; the API checks a lone
// `pricing` or `price` against the stored ticket type.
export const UpdateTicketTypeRequestValidationSchema = withFreeTicketTypePriceCheck(
  TicketTypeInputValidationSchema.partial()
    .omit({ key: true })
    .extend({
      ticketGroupId: ObjectIdStringValidationSchema.optional().openapi({
        description:
          'Destination ticket group ID. When provided, the ticket type is moved to this ticket group before applying the remaining updates.',
        example: '507f1f77bcf86cd799439015',
      }),
      triggerTicketTypeId: ObjectIdStringValidationSchema.nullish().openapi({
        description: "Ticket type ID that triggers this type's availability.",
        example: '507f1f77bcf86cd799439014',
      }),
      showHideLogic: TicketTypeShowHideLogicInputValidationSchema.nullish(),
    }),
).openapi('UpdateTicketTypeRequest');

// The body's ticketGroupId is the move destination; the tool input spends that name on the
// path's current group, so the destination travels as moveToTicketGroupId.
const { ticketGroupId: _destinationTicketGroupId, ...ticketTypeUpdateFieldsShape } =
  UpdateTicketTypeRequestValidationSchema.shape;

const ticketTypeUpdateToolShape = {
  ...ticketTypeUpdateFieldsShape,
  imageId: ObjectIdStringValidationSchema.nullish().meta({
    description: `${TICKET_TYPE_IMAGE_ID_DESCRIPTION} Pass null to remove the current image.`,
  }),
  triggerTicketTypeId: ObjectIdStringValidationSchema.nullish().meta({
    description: `${TICKET_TYPE_TRIGGER_ID_DESCRIPTION} Pass null to remove the trigger.`,
  }),
};

export const TICKET_TYPE_UPDATE_FIELDS = Object.keys(ticketTypeUpdateToolShape) as ReadonlyArray<
  keyof typeof ticketTypeUpdateToolShape
>;

export const UpdateTicketTypeInputValidationSchema = withFreeTicketTypePriceCheck(
  z
    .object({
      pageId: TicketTypeEventIdInputSchema,
      ticketGroupId: ObjectIdStringValidationSchema.meta({
        description:
          'The ID of the ticket group the ticket type is in now, from `ticketGroups[].id` in get_event. This does not move it: use moveToTicketGroupId for that.',
      }),
      ticketTypeId: TicketTypeIdInputSchema,
      ...ticketTypeUpdateToolShape,
      moveToTicketGroupId: ObjectIdStringValidationSchema.optional().meta({
        description:
          'The ID of another ticket group on the same event to move the ticket type to, from `ticketGroups[].id` in get_event. The move happens before the other changes. A ticket type that has sold tickets cannot be moved.',
      }),
    })
    .refine(
      value => {
        const {
          pageId: _pageId,
          ticketGroupId: _ticketGroupId,
          ticketTypeId: _ticketTypeId,
          ...changes
        } = value;
        return hasAtLeastOneField(changes);
      },
      {
        message:
          'At least one ticket type property to update, or moveToTicketGroupId, must be provided.',
      },
    ),
);

export type UpdateTicketTypeInput = z.infer<typeof UpdateTicketTypeInputValidationSchema>;
